import React, { useState, useEffect, useRef } from 'react';
import { 
  Music, 
  Search, 
  Filter, 
  Plus, 
  Edit2, 
  Trash2, 
  Calendar,
  Globe,
  Instagram,
  Download,
  Upload,
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  Star,
  Link as LinkIcon,
  Mail,
  Phone,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  User
} from 'lucide-react';
import { api } from '../utils/api';
import Modal from '../components/Modal';
import { exportToCSV } from '../utils/export';

const ArtistsPage = () => {
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterGenre, setFilterGenre] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [expandedArtist, setExpandedArtist] = useState(null);
  const [viewingArtist, setViewingArtist] = useState(null);
  const [editingArtist, setEditingArtist] = useState(null);
  const [deletingArtist, setDeletingArtist] = useState(null);
  const fileInputRef = useRef(null);

  const genres = ['all', 'Minimal', 'Tech House', 'Deep House', 'Progressive', 'Techno', 'Melodic'];
  const statuses = ['all', 'pending', 'approved', 'featured', 'inactive'];

  useEffect(() => {
    loadArtists();
  }, []);

  const loadArtists = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.artists.getAll();
      setArtists(response.artists || []);
    } catch (error) {
      console.error('Error loading artists:', error);
      setError('Failed to load artists');
    } finally {
      setLoading(false);
    }
  };

  const generateFallbackAvatar = (name) => {
    // Generate a simple avatar with initials if no profile picture
    const initials = name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    const colors = ['#2D6B42', '#D4A574', '#4A90E2', '#7B68EE', '#20B2AA'];
    const colorIndex = name.length % colors.length;
    const bgColor = colors[colorIndex];
    
    return (
      <div 
        className="w-full h-full rounded-lg flex items-center justify-center text-white font-medium text-sm"
        style={{ backgroundColor: bgColor }}
      >
        {initials}
      </div>
    );
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'pending':
        return <span className="flex items-center gap-1 text-yellow-500 text-xs">
          <Clock size={14} /> Pending Review
        </span>;
      case 'approved':
        return <span className="flex items-center gap-1 text-green-500 text-xs">
          <CheckCircle size={14} /> Approved
        </span>;
      case 'featured':
        return <span className="flex items-center gap-1 text-[#D4A574] text-xs">
          <Star size={14} /> Featured
        </span>;
      case 'inactive':
        return <span className="flex items-center gap-1 text-[#888888] text-xs">
          <XCircle size={14} /> Inactive
        </span>;
      default:
        return null;
    }
  };

  const handleApproveArtist = async (artistId) => {
    try {
      await api.artists.update(artistId, { status: 'approved' });
      await loadArtists();
    } catch (error) {
      console.error('Error approving artist:', error);
      setError('Failed to approve artist');
    }
  };

  const handleDeleteArtist = async () => {
    try {
      await api.artists.delete(deletingArtist.id);
      setArtists(artists.filter(a => a.id !== deletingArtist.id));
      setDeletingArtist(null);
    } catch (error) {
      console.error('Error deleting artist:', error);
      setError('Failed to delete artist');
    }
  };

  const handleExportArtists = () => {
    const exportData = filteredArtists.map(artist => ({
      Name: artist.name,
      Email: artist.email,
      Phone: artist.phone || '',
      Genre: artist.genres?.join(', ') || '',
      Status: artist.status,
      Instagram: artist.social?.instagram || '',
      Website: artist.website || '',
      'Available for': artist.availability?.join(', ') || '',
      'Application Date': new Date(artist.createdAt).toLocaleDateString()
    }));
    
    exportToCSV(exportData, 'artists.csv');
  };

  const filteredArtists = artists.filter(artist => {
    const matchesSearch = artist.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         artist.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesGenre = filterGenre === 'all' || artist.genres?.includes(filterGenre);
    const matchesStatus = filterStatus === 'all' || artist.status === filterStatus;
    return matchesSearch && matchesGenre && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4A574] mx-auto mb-4"></div>
          <p className="text-[#888888]">Loading artists...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-light mb-2">Artists</h1>
          <p className="text-[#888888]">Manage artist profiles and applications</p>
        </div>
        <div className="flex gap-3">
          <a 
            href="/artists/apply" 
            target="_blank"
            className="btn-secondary flex items-center gap-2"
          >
            <LinkIcon size={20} />
            Application Link
          </a>
          <button 
            onClick={handleExportArtists}
            className="btn-secondary flex items-center gap-2"
          >
            <Download size={20} />
            Export
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-4 bg-red-500/10 border border-red-500 rounded-lg flex items-center gap-2">
          <AlertCircle size={20} className="text-red-500" />
          <p className="text-red-500">{error}</p>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card-dark text-center py-4">
          <p className="text-2xl font-light">{artists.length}</p>
          <p className="text-sm text-[#888888]">Total Artists</p>
        </div>
        <div className="card-dark text-center py-4">
          <p className="text-2xl font-light text-yellow-500">
            {artists.filter(a => a.status === 'pending').length}
          </p>
          <p className="text-sm text-[#888888]">Pending Review</p>
        </div>
        <div className="card-dark text-center py-4">
          <p className="text-2xl font-light text-green-500">
            {artists.filter(a => a.status === 'approved').length}
          </p>
          <p className="text-sm text-[#888888]">Approved</p>
        </div>
        <div className="card-dark text-center py-4">
          <p className="text-2xl font-light text-[#D4A574]">
            {artists.filter(a => a.status === 'featured').length}
          </p>
          <p className="text-sm text-[#888888]">Featured</p>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-3 text-[#888888]" size={20} />
          <input
            type="text"
            placeholder="Search artists..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input-dark w-full pl-10"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="text-[#888888]" size={20} />
          <select
            value={filterGenre}
            onChange={(e) => setFilterGenre(e.target.value)}
            className="input-dark"
          >
            {genres.map(genre => (
              <option key={genre} value={genre}>
                {genre === 'all' ? 'All Genres' : genre}
              </option>
            ))}
          </select>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="input-dark"
          >
            {statuses.map(status => (
              <option key={status} value={status}>
                {status === 'all' ? 'All Status' : status.charAt(0).toUpperCase() + status.slice(1)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Artists Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {filteredArtists.map((artist) => (
          <div key={artist.id} className="card-dark">
            {/* Artist Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 bg-[#0A0A0A] rounded-lg flex items-center justify-center overflow-hidden">
                  {artist.profilePicture ? (
                    <img src={artist.profilePicture} alt={artist.name} className="w-full h-full rounded-lg object-cover" />
                  ) : artist.profilePictureData?.data ? (
                    <img src={artist.profilePictureData.data} alt={artist.name} className="w-full h-full rounded-lg object-cover" />
                  ) : artist.name ? (
                    generateFallbackAvatar(artist.name)
                  ) : (
                    <User className="text-[#888888]" size={24} />
                  )}
                </div>
                <div>
                  <h3 className="text-xl font-medium mb-1">{artist.name}</h3>
                  {getStatusBadge(artist.status)}
                  {artist.genres && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {artist.genres.map(genre => (
                        <span key={genre} className="text-xs px-2 py-1 bg-[#0A0A0A] rounded">
                          {genre}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <button 
                onClick={() => setExpandedArtist(expandedArtist === artist.id ? null : artist.id)}
                className="p-2 hover:bg-[#0A0A0A] rounded-lg transition-colors"
              >
                {expandedArtist === artist.id ? <ChevronUp /> : <ChevronDown />}
              </button>
            </div>

            {/* Quick Info */}
            <div className="space-y-2 text-sm">
              {artist.email && (
                <div className="flex items-center gap-2 text-[#888888]">
                  <Mail size={16} />
                  <span>{artist.email}</span>
                </div>
              )}
              {artist.social?.instagram && (
                <div className="flex items-center gap-2 text-[#888888]">
                  <Instagram size={16} />
                  <span>@{artist.social.instagram}</span>
                </div>
              )}
            </div>

            {/* Expanded Details */}
            {expandedArtist === artist.id && (
              <div className="mt-6 pt-6 border-t border-[#333333] space-y-4 animate-slide-up">
                {artist.bio && (
                  <div>
                    <h4 className="text-sm font-medium text-[#888888] mb-2">Bio</h4>
                    <p className="text-sm">{artist.bio}</p>
                  </div>
                )}
                
                {artist.mixLinks && artist.mixLinks.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-[#888888] mb-2">Mix Links</h4>
                    <div className="space-y-1">
                      {artist.mixLinks.map((link, index) => (
                        <a 
                          key={index}
                          href={link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-[#D4A574] hover:underline flex items-center gap-1"
                        >
                          <LinkIcon size={14} />
                          {link}
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {artist.availability && (
                  <div>
                    <h4 className="text-sm font-medium text-[#888888] mb-2">Available For</h4>
                    <div className="flex flex-wrap gap-2">
                      {artist.availability.map(item => (
                        <span key={item} className="text-xs px-2 py-1 bg-[#0A0A0A] rounded">
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2 pt-4">
                  {artist.status === 'pending' && (
                    <button 
                      onClick={() => handleApproveArtist(artist.id)}
                      className="btn-primary text-sm flex items-center gap-2"
                    >
                      <CheckCircle size={16} />
                      Approve
                    </button>
                  )}
                  <button 
                    onClick={() => setViewingArtist(artist)}
                    className="btn-secondary text-sm flex items-center gap-2"
                  >
                    <Eye size={16} />
                    View Full Profile
                  </button>
                  <button 
                    onClick={() => setDeletingArtist(artist)}
                    className="btn-secondary text-sm text-red-500 hover:bg-red-500/10 flex items-center gap-2"
                  >
                    <Trash2 size={16} />
                    Delete
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Empty State */}
      {filteredArtists.length === 0 && (
        <div className="card-dark text-center py-12">
          <Music className="mx-auto text-[#333333] mb-4" size={48} />
          <p className="text-[#888888]">No artists found</p>
        </div>
      )}

      {/* View Artist Modal */}
      <Modal
        isOpen={!!viewingArtist}
        onClose={() => setViewingArtist(null)}
        title="Artist Profile"
        maxWidth="max-w-3xl"
      >
        {viewingArtist && (
          <div className="space-y-6">
            <div className="flex items-start gap-6">
              <div className="w-24 h-24 bg-[#0A0A0A] rounded-lg flex items-center justify-center">
                {viewingArtist.profileImage ? (
                  <img src={viewingArtist.profileImage} alt={viewingArtist.name} className="w-full h-full rounded-lg object-cover" />
                ) : (
                  <Music className="text-[#888888]" size={32} />
                )}
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-light mb-2">{viewingArtist.name}</h3>
                {getStatusBadge(viewingArtist.status)}
                {viewingArtist.genres && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {viewingArtist.genres.map(genre => (
                      <span key={genre} className="px-3 py-1 bg-[#0A0A0A] rounded text-sm">
                        {genre}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Full profile details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-sm font-medium text-[#888888] mb-3">Contact Information</h4>
                <div className="space-y-2 text-sm">
                  {viewingArtist.email && (
                    <div className="flex items-center gap-2">
                      <Mail size={16} className="text-[#888888]" />
                      <a href={`mailto:${viewingArtist.email}`} className="hover:text-[#D4A574]">
                        {viewingArtist.email}
                      </a>
                    </div>
                  )}
                  {viewingArtist.phone && (
                    <div className="flex items-center gap-2">
                      <Phone size={16} className="text-[#888888]" />
                      <span>{viewingArtist.phone}</span>
                    </div>
                  )}
                  {viewingArtist.website && (
                    <div className="flex items-center gap-2">
                      <Globe size={16} className="text-[#888888]" />
                      <a href={viewingArtist.website} target="_blank" rel="noopener noreferrer" className="hover:text-[#D4A574]">
                        {viewingArtist.website}
                      </a>
                    </div>
                  )}
                  {viewingArtist.social?.instagram && (
                    <div className="flex items-center gap-2">
                      <Instagram size={16} className="text-[#888888]" />
                      <a href={`https://instagram.com/${viewingArtist.social.instagram}`} target="_blank" rel="noopener noreferrer" className="hover:text-[#D4A574]">
                        @{viewingArtist.social.instagram}
                      </a>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <h4 className="text-sm font-medium text-[#888888] mb-3">Application Details</h4>
                <div className="space-y-2 text-sm">
                  <p><span className="text-[#888888]">Applied:</span> {new Date(viewingArtist.createdAt).toLocaleDateString()}</p>
                  {viewingArtist.approvedAt && (
                    <p><span className="text-[#888888]">Approved:</span> {new Date(viewingArtist.approvedAt).toLocaleDateString()}</p>
                  )}
                  {viewingArtist.availability && (
                    <div>
                      <span className="text-[#888888]">Available for:</span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {viewingArtist.availability.map(item => (
                          <span key={item} className="text-xs px-2 py-1 bg-[#0A0A0A] rounded">
                            {item}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {viewingArtist.bio && (
              <div>
                <h4 className="text-sm font-medium text-[#888888] mb-3">Biography</h4>
                <p className="text-sm">{viewingArtist.bio}</p>
              </div>
            )}

            {viewingArtist.mixLinks && viewingArtist.mixLinks.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-[#888888] mb-3">Mix Links</h4>
                <div className="space-y-2">
                  {viewingArtist.mixLinks.map((link, index) => (
                    <a 
                      key={index}
                      href={link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-[#D4A574] hover:underline flex items-center gap-2"
                    >
                      <LinkIcon size={16} />
                      {link}
                    </a>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-3 pt-4 border-t border-[#333333]">
              <button
                onClick={() => setViewingArtist(null)}
                className="btn-secondary"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!deletingArtist}
        onClose={() => setDeletingArtist(null)}
        title="Delete Artist"
        maxWidth="max-w-md"
      >
        <div className="space-y-4">
          <p>Are you sure you want to delete <strong>{deletingArtist?.name}</strong>?</p>
          <p className="text-sm text-[#888888]">This action cannot be undone.</p>
          
          <div className="flex gap-3 justify-end pt-4">
            <button
              onClick={() => setDeletingArtist(null)}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteArtist}
              className="btn-primary bg-red-500 hover:bg-red-600"
            >
              Delete Artist
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ArtistsPage;